/**
 * Source attribution capture for OperatorAI.
 *
 * Rules:
 *  - On every page load, read UTM params + referrer + landing path from the URL.
 *  - If UTM params are present in THIS visit, overwrite stored attribution
 *    (latest-touch wins — matches what most marketers actually look at).
 *  - If no UTM params on first arrival, store referrer/landing only (first-touch
 *    fallback). Don't overwrite previously stored UTMs on subsequent navigations
 *    inside the same session — the user is already attributed.
 *  - When the visitor submits the signup form, send the attribution payload
 *    along with the email.
 */

const STORAGE_KEY = "operatorai_attribution";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

const readQuery = () => {
  if (typeof window === "undefined") return {};
  const sp = new URLSearchParams(window.location.search);
  // Allow short alias ?src=tiktok as a friendlier alternative to full UTM
  const out = {};
  UTM_KEYS.forEach((k) => {
    const v = sp.get(k);
    if (v) out[k] = v.toLowerCase().trim().slice(0, 60);
  });
  const short = sp.get("src");
  if (short && !out.utm_source) {
    out.utm_source = short.toLowerCase().trim().slice(0, 60);
    out.utm_medium = out.utm_medium || "social";
  }
  return out;
};

const safeParse = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/**
 * Call once on app startup to capture/refresh attribution.
 * Idempotent — safe to call from multiple components.
 */
export const captureAttribution = () => {
  if (typeof window === "undefined") return;
  const stored = safeParse(sessionStorage.getItem(STORAGE_KEY)) || {};
  const fromUrl = readQuery();

  const hasNewUtm = Object.keys(fromUrl).some((k) => fromUrl[k]);
  const next = {
    // First-touch metadata
    referrer: stored.referrer || (document.referrer || "").slice(0, 200),
    landing_path:
      stored.landing_path ||
      `${window.location.pathname}${window.location.search}`.slice(0, 200),
    captured_at: stored.captured_at || new Date().toISOString(),
    // Last-touch UTM (overwrite when new UTMs arrive)
    ...(hasNewUtm
      ? fromUrl
      : {
          utm_source: stored.utm_source,
          utm_medium: stored.utm_medium,
          utm_campaign: stored.utm_campaign,
          utm_content: stored.utm_content,
          utm_term: stored.utm_term,
        }),
  };

  // Drop undefined keys
  Object.keys(next).forEach((k) => next[k] == null && delete next[k]);
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  return next;
};

/**
 * Read current attribution for sending with a form submit.
 */
export const getAttribution = () => {
  if (typeof window === "undefined") return {};
  return safeParse(sessionStorage.getItem(STORAGE_KEY)) || {};
};
